import { useMemo } from 'react'
import { useAuth } from './use-auth'

/**
 * Mapeamento de rotas para identificadores de acesso.
 * Use esses identificadores no campo 'acessos' da tabela permissao.
 */
export const MENU_KEYS = {
  home: 'home',
  dashboard: 'dashboard',
  financeiro: 'financeiro',
  clientes: 'clientes',
  configuracoes: 'configuracoes',
  dre: 'dre',
  'relatorio-vendas': 'relatorio-vendas',
  'dados-historicos': 'dados-historicos',
  grupos: 'grupos',
  'metas-loja': 'metas-loja',
  'metas-individuais': 'metas-individuais',
  'fluxo-caixa': 'fluxo-caixa',
  estoque: 'estoque',
  fotos: 'fotos',
  perfil: 'perfil',
} as const

export type MenuKey = keyof typeof MENU_KEYS

/**
 * Hook para verificar acesso a menus.
 * @param menuKey - Identificador do menu (ex: "grupos", "clientes")
 * @returns true se o usuário pode acessar o menu
 */
export function useMenuAccess(menuKey: string): boolean {
  const { hasMenuAccess } = useAuth()
  return hasMenuAccess(menuKey)
}

/**
 * Acesso total aos dados: `permissao.acessos` contém "all".
 *
 * Regra de escopo (tabela `permissao`):
 * - `gerencia` (acessos `["all"]`)  → visão global: dados de todos os vendedores.
 * - Qualquer outro perfil (`vendedor`, `supervisao`) → apenas os próprios dados.
 *
 * Fonte única para decidir escopo de dados. Não usar `permissao_id`: o id é
 * acoplamento a uma linha específica do banco e já causou supervisão cair na
 * visão de gerência.
 */
export function useHasFullAccess(): boolean {
  const { acessos } = useAuth()
  return acessos.includes('all')
}

/**
 * Hook para obter lista de menus acessíveis.
 * @returns Objeto com hasAccess(key) e lista de acessos
 */
export function useMenuAccessList() {
  const { acessos, hasMenuAccess } = useAuth()

  const accessMap = useMemo(() => {
    return Object.keys(MENU_KEYS).reduce(
      (acc, key) => {
        acc[key] = hasMenuAccess(key)
        return acc
      },
      {} as Record<string, boolean>
    )
  }, [hasMenuAccess])

  return {
    acessos,
    hasAccess: hasMenuAccess,
    accessMap,
    hasFullAccess: acessos.includes('all'),
  }
}

/**
 * Extrai o identificador de menu a partir de uma rota.
 * Ex: "/clientes" -> "clientes", "/dados-historicos" -> "dados-historicos"
 */
export function getMenuKeyFromPath(path: string): string {
  // Remove barra inicial e pega o primeiro segmento
  const segments = path.replace(/^\//, '').split('/').filter(Boolean)
  const firstSegment = segments[0] ?? 'home'

  // Mapeia rotas aninhadas para o menu correto
  if (firstSegment === 'cadastro') {
    return 'clientes'
  }

  return firstSegment
}
